/**
 * DataTable (DESIGN.md T1): TanStack Table with sorting, row selection, active row (the one
 * shown in the DetailPanel), loading skeleton and empty state. Row height follows density.
 *
 * Responsive (§9.1 table, T1 "card list" variant): ≥ md a table, < md a card list. The first
 * column is the card title, the other columns are label/value lines. A row click opens the
 * DetailPanel, which is a full-screen Sheet on phones, so phones show one pane at a time.
 *
 * Column options (`meta`): `numeric` right-aligns with tabular numerals (sizes, counts, quotas),
 * `align: 'end'` right-aligns other content, `label` names the column in the card list when the
 * header is not a plain string.
 *
 * Row actions (`rowMenu`, DESIGN.md §18): context menu (right-click, Shift+F10, long-press) and a
 * "…" column, both from the same registry entries.
 *
 * Ordered lists (`reorder`, T1 "ordered" variant: rules, priorities): a drag handle column; drag a row onto another
 * row, or focus the handle and press ArrowUp/ArrowDown. Sorting is off (the order is the data). The page keeps the
 * new order as a draft and saves it explicitly; phones move rows through the row menu.
 *
 * Virtualization (`virtualize`): long lists (trace results, logs) render only the visible rows inside a
 * scroll area of their own once they have more than VIRTUAL_MIN_ROWS rows; short lists stay plain tables.
 * Both options combine (a virtualized ordered list keeps drag + keyboard moves on the rendered rows).
 *
 * Not yet: column resize/chooser, sticky header inside a scroll area. Add them here as props so every
 * list gets them at once.
 */
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type Column,
  type ColumnDef,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type Table as TanStackTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, GripVerticalIcon } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ActionOverflowMenu } from '../actions/action-menu';
import { ContextMenuArea, type ContextMenuSpec } from '../actions/context-menu-area';
import { contextTargets } from '../actions/registry';
import { Checkbox } from '../components/checkbox';
import { Skeleton } from '../components/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { PagingFooter, type PagingState } from './paging-footer';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';

export type { ColumnDef } from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  // Type parameters must match TanStack's declaration for the merge.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Horizontal alignment of header and cells (default `start`). */
    align?: 'start' | 'end';
    /** Numbers (sizes, counts, quotas): right-aligned with tabular numerals. */
    numeric?: boolean;
    /** Column name in the phone card list when `header` is not a plain string. */
    label?: string;
  }
}

/** Lists with more rows than this render virtualized when `virtualize` is set. */
export const VIRTUAL_MIN_ROWS = 100;
/** Row height estimate before a row is measured (comfortable density). */
const ESTIMATED_ROW_PX = 40;

const SELECT_COLUMN = '__select';
const ORDER_COLUMN = '__order';
/** dataTransfer type of a dragged row id (only rows of an ordered DataTable accept it). */
const ROW_DRAG_TYPE = 'application/x-platform-row';
const ACTIONS_COLUMN = '__actions';
const isControlColumn = (id: string) => id === SELECT_COLUMN || id === ACTIONS_COLUMN || id === ORDER_COLUMN;

/** Alignment classes for a column's header and cells (table and card list). */
function alignment<T>(column: Column<T, unknown>): string | undefined {
  const meta = column.columnDef.meta;
  return cn((meta?.numeric || meta?.align === 'end') && 'text-right', meta?.numeric && 'tabular-nums');
}

function columnLabel<T>(column: Column<T, unknown>): string {
  const { meta, header } = column.columnDef;
  return meta?.label ?? (typeof header === 'string' ? header : '');
}

const renderCell = <T,>(cell: Cell<T, unknown>) => flexRender(cell.column.columnDef.cell, cell.getContext());

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: readonly T[];
  getRowId: (row: T) => string;
  loading?: boolean;
  /** Shown when there are no rows (after filtering). */
  empty?: ReactNode;
  /** Quick filter over all columns (CommandBar search). */
  globalFilter?: string;
  /** Adds a checkbox column. */
  selectable?: boolean;
  onSelectionChange?: (ids: string[]) => void;
  /** Row click (not on the checkbox) — opens the DetailPanel (T2). */
  onRowClick?: (row: T) => void;
  /** Id of the row shown in the DetailPanel. */
  activeRowId?: string;
  /** Accessible table name. */
  label: string;
  /** Narrow detail panels use cards based on their own width, independently of the viewport. */
  containerResponsive?: boolean;
  skeletonRows?: number;
  /**
   * Row actions (DESIGN.md §18) from the app's action registry. Right-click, Shift+F10 and
   * long-press open them for the targets (the selection when the row is selected, else the row,
   * which then becomes the selection); a "…" button per row offers them for that row.
   */
  rowMenu?: (rows: T[]) => ContextMenuSpec | undefined;
  /** Render only the visible rows (table layout, more than VIRTUAL_MIN_ROWS rows) in a scroll area of its own. */
  virtualize?: boolean;
  /** Ordered list: drag handle column + keyboard moves; disables sorting. */
  reorder?: DataTableReorder<T>;
  /**
   * Server-paged rows: pass the `useInfiniteList` result; the next page is requested while the end nears
   * (sentinel after the last row), with a "Load more" button as the keyboard fallback.
   */
  paging?: PagingState;
}

/** Ordered-list variant of the DataTable (rules, priorities). */
export interface DataTableReorder<T> {
  /** Move the row with this id to `toIndex` (index in `data`, already clamped to the list). */
  onMove: (id: string, toIndex: number) => void;
  /** Rows that keep their place (e.g. a fixed built-in rule): no handle, no drop target. Default: all move. */
  canMove?: (row: T) => boolean;
  /** Accessible name of the row's handle, e.g. "Move rule Block executables" (labels come from the app). */
  handleLabel: (row: T) => string;
}

/** What both layouts (table, card list) need from the DataTable. */
interface ViewProps<T> {
  table: TanStackTable<T>;
  rows: Row<T>[];
  label: string;
  loading: boolean;
  skeletonRows: number;
  emptyContent: ReactNode;
  activeRowId?: string;
  onRowClick?: (row: T) => void;
  virtualize: boolean;
  reorder?: DataTableReorder<T>;
  paging?: PagingState;
  rowCount: number;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  loading = false,
  empty,
  globalFilter,
  selectable = false,
  onSelectionChange,
  onRowClick,
  activeRowId,
  label,
  skeletonRows = 6,
  rowMenu,
  virtualize = false,
  reorder,
  paging,
  containerResponsive = false,
}: DataTableProps<T>) {
  const strings = useUiStrings();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const container = useRef<HTMLDivElement>(null);
  const [containerWide, setContainerWide] = useState(false);
  useEffect(() => {
    if (!containerResponsive || !container.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWide(entry.contentRect.width >= 640);
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [containerResponsive]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const selectColumn: ColumnDef<T, unknown> = {
    id: SELECT_COLUMN,
    enableSorting: false,
    header: ({ table }) => <SelectAllCheckbox table={table} />,
    cell: RowCheckbox,
  };

  // "…" per row: the path to the row actions without right-click (DESIGN.md §18).
  const actionsColumn: ColumnDef<T, unknown> = {
    id: ACTIONS_COLUMN,
    enableSorting: false,
    header: () => <span className="sr-only">{strings.actions}</span>,
    meta: { align: 'end' },
    cell: ({ row }) => {
      const spec = rowMenu?.([row.original]);
      if (!spec) return null;
      return (
        // The menu is portaled but its events bubble through React: keep them away from the row click.
        <span className="inline-flex" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <ActionOverflowMenu actions={spec.actions} title={spec.title} size="icon-sm" />
        </span>
      );
    },
  };

  const changeSelection = (next: RowSelectionState) => {
    setRowSelection(next);
    onSelectionChange?.(Object.keys(next).filter((id) => next[id]));
  };

  // Ordered variant: a handle per movable row (drag source; ArrowUp/ArrowDown move by one).
  const orderColumn: ColumnDef<T, unknown> | undefined = reorder && {
    id: ORDER_COLUMN,
    enableSorting: false,
    header: () => null,
    cell: ({ row }) => {
      if (reorder.canMove?.(row.original) === false) return null;
      const move = (toIndex: number) => {
        if (toIndex < 0 || toIndex >= data.length) return;
        reorder.onMove(row.id, toIndex);
        // The row moved in the DOM: give the focus back to its handle.
        requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-reorder-handle="${CSS.escape(row.id)}"]`)?.focus());
      };
      return (
        <button
          type="button"
          draggable
          data-reorder-handle={row.id}
          aria-label={reorder.handleLabel(row.original)}
          title={reorder.handleLabel(row.original)}
          className="inline-flex cursor-grab items-center rounded p-1 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
            event.preventDefault();
            event.stopPropagation();
            move(row.index + (event.key === 'ArrowUp' ? -1 : 1));
          }}
          onDragStart={(event) => {
            event.dataTransfer.setData(ROW_DRAG_TYPE, row.id);
            event.dataTransfer.effectAllowed = 'move';
          }}
        >
          <GripVerticalIcon className="size-4" aria-hidden />
        </button>
      );
    },
  };

  // TanStack Table returns non-memoizable functions by design; the compiler rule does not apply.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable<T>({
    data: data as T[],
    columns: [...(orderColumn ? [orderColumn] : []), ...(selectable ? [selectColumn] : []), ...columns, ...(rowMenu ? [actionsColumn] : [])],
    state: { sorting, rowSelection, globalFilter },
    enableSorting: !reorder,
    getRowId: (row) => getRowId(row),
    enableRowSelection: selectable,
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => changeSelection(typeof updater === 'function' ? updater(rowSelection) : updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const menuFor = (id: string) => {
    if (!rowMenu) return undefined;
    const selected = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    const targets = selectable ? contextTargets(selected, id) : { ids: [id], changed: false };
    if (targets.changed) changeSelection({ [id]: true });
    const byId = table.getCoreRowModel().rowsById;
    return rowMenu(targets.ids.flatMap((target) => (byId[target] ? [byId[target].original] : [])));
  };

  const view: ViewProps<T> = {
    table,
    rows: table.getRowModel().rows,
    label,
    loading,
    skeletonRows,
    emptyContent: empty ?? <p className="py-8 text-center text-muted-foreground">{strings.noResults}</p>,
    activeRowId,
    onRowClick,
    virtualize,
    reorder,
    paging,
    rowCount: data.length,
  };
  const content = <ContextMenuArea menuFor={rowMenu && menuFor}>{(containerResponsive ? containerWide : isDesktop) ? <TableView {...view} /> : <CardList {...view} />}</ContextMenuArea>;
  return containerResponsive ? <div ref={container} data-slot="data-table-responsive" className="min-w-0 w-full">{content}</div> : content;
}

/** Drop target props of a row in an ordered table (drag a handle onto a row = take its place). */
function dropTarget<T>(row: Row<T>, reorder: DataTableReorder<T> | undefined) {
  if (!reorder || reorder.canMove?.(row.original) === false) return {};
  const accepts = (event: DragEvent) => event.dataTransfer.types.includes(ROW_DRAG_TYPE);
  return {
    onDragOver: (event: DragEvent) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    onDrop: (event: DragEvent) => {
      const id = accepts(event) ? event.dataTransfer.getData(ROW_DRAG_TYPE) : '';
      if (!id || id === row.id) return;
      event.preventDefault();
      reorder.onMove(id, row.index);
    },
  };
}

// flexRender uses the renderer as a React component type. Keep it stable so a
// table update between pointer down and pointer up does not replace the target.
function RowCheckbox<T>({ row }: { row: Row<T> }) {
  const strings = useUiStrings();
  return (
    <Checkbox
      aria-label={strings.selectRow}
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(value === true)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    />
  );
}

function SelectAllCheckbox<T>({ table }: { table: TanStackTable<T> }) {
  const strings = useUiStrings();
  return (
    <Checkbox
      aria-label={strings.selectAll}
      checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false}
      onCheckedChange={(value) => table.toggleAllRowsSelected(value === true)}
    />
  );
}

/** Click and Enter open the row (DetailPanel); shared by table rows and cards. */
function rowInteraction<T>(row: Row<T>, onRowClick?: (row: T) => void) {
  if (!onRowClick) return {};
  return {
    tabIndex: 0,
    onClick: () => onRowClick(row.original),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter') onRowClick(row.original);
    },
  };
}

const rowState = <T,>(row: Row<T>, activeRowId?: string) => (row.getIsSelected() || row.id === activeRowId ? 'selected' : undefined);

function TableView<T>({ table, rows, label, loading, skeletonRows, emptyContent, activeRowId, onRowClick, virtualize, reorder, paging, rowCount }: ViewProps<T>) {
  const strings = useUiStrings();
  const columnCount = table.getVisibleLeafColumns().length;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtual = virtualize && rows.length > VIRTUAL_MIN_ROWS;
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual is not compiler-safe; this component is not memoized
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_PX,
    overscan: 12,
    getItemKey: (index) => rows[index]?.id ?? index,
  });
  const windowItems = virtual ? virtualizer.getVirtualItems() : [];
  const visible = virtual
    ? windowItems.flatMap((item) => {
        const row = rows[item.index];
        return row ? [{ row, index: item.index }] : [];
      })
    : rows.map((row, index) => ({ row, index }));
  // Spacer rows keep the scroll height of the whole list around the rendered window.
  const padTop = windowItems[0]?.start ?? 0;
  const padBottom = virtual ? virtualizer.getTotalSize() - (windowItems.at(-1)?.end ?? 0) : 0;

  return (
    <div ref={scrollRef} className={cn('rounded-lg border bg-card', virtual && 'max-h-[70vh] overflow-y-auto')}>
      <Table aria-label={label} aria-busy={loading}>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                const content = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
                return (
                  <TableHead
                    key={header.id}
                    className={alignment(header.column)}
                    aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="-mx-1 inline-flex items-center gap-1 rounded px-1 outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
                      >
                        {content}
                        {sorted === 'asc' ? (
                          <ArrowUpIcon className="size-3" aria-label={strings.sortAscending} />
                        ) : sorted === 'desc' ? (
                          <ArrowDownIcon className="size-3" aria-label={strings.sortDescending} />
                        ) : (
                          <ArrowUpDownIcon aria-hidden className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      content
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading && rows.length === 0 ? (
            Array.from({ length: skeletonRows }, (_, index) => (
              <TableRow key={index} className="hover:bg-transparent">
                {Array.from({ length: columnCount }, (_, cell) => (
                  <TableCell key={cell}>
                    <Skeleton className="h-3 w-3/4" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columnCount} className="whitespace-normal">
                {emptyContent}
              </TableCell>
            </TableRow>
          ) : (
            <>
              {padTop > 0 && <tr aria-hidden style={{ height: padTop }} />}
              {visible.map(({ row, index }) => (
                <TableRow
                  key={row.id}
                  ref={virtual ? virtualizer.measureElement : undefined}
                  data-index={index}
                  data-state={rowState(row, activeRowId)}
                  data-context-id={row.id}
                  aria-selected={row.id === activeRowId}
                  {...rowInteraction(row, onRowClick)}
                  {...dropTarget(row, reorder)}
                  className={cn(onRowClick && 'cursor-pointer outline-none focus-visible:bg-accent')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={alignment(cell.column)}>
                      {renderCell(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {padBottom > 0 && <tr aria-hidden style={{ height: padBottom }} />}
            </>
          )}
        </TableBody>
      </Table>
      <PagingFooter paging={paging} rowCount={rowCount} rootRef={virtual ? scrollRef : undefined} />
    </div>
  );
}

/** Phone layout (< md): one card per row, title = first column, other columns as label/value lines. */
function CardList<T>({ table, rows, label, loading, skeletonRows, emptyContent, activeRowId, onRowClick, paging, rowCount }: ViewProps<T>) {
  const strings = useUiStrings();
  const selectable = table.getAllLeafColumns().some((column) => column.id === SELECT_COLUMN);

  return (
    <div className="flex flex-col gap-2">
      {selectable && rows.length > 0 && (
        <label className="flex items-center gap-2 px-3 text-sm text-muted-foreground">
          <SelectAllCheckbox table={table} />
          {strings.selectAll}
        </label>
      )}
      <ul aria-label={label} aria-busy={loading} className="flex flex-col gap-2">
        {loading && rows.length === 0 ? (
          Array.from({ length: skeletonRows }, (_, index) => (
            <li key={index} className="flex flex-col gap-2 rounded-lg border bg-card p-3">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-3/4" />
            </li>
          ))
        ) : rows.length === 0 ? (
          <li className="rounded-lg border bg-card p-3">{emptyContent}</li>
        ) : (
          rows.map((row) => {
            const cells = row.getVisibleCells();
            const select = cells.find((cell) => cell.column.id === SELECT_COLUMN);
            const actions = cells.find((cell) => cell.column.id === ACTIONS_COLUMN);
            const [title, ...details] = cells.filter((cell) => !isControlColumn(cell.column.id));
            return (
              <li
                key={row.id}
                data-state={rowState(row, activeRowId)}
                data-context-id={row.id}
                aria-current={row.id === activeRowId ? 'true' : undefined}
                {...rowInteraction(row, onRowClick)}
                className={cn(
                  'flex touch-callout-none flex-col gap-2 rounded-lg border bg-card p-3 text-density transition-colors data-[state=selected]:bg-accent',
                  onRowClick && 'cursor-pointer outline-none hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/50',
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  {select && renderCell(select)}
                  {title && <div className="min-w-0 flex-1 truncate font-medium">{renderCell(title)}</div>}
                  {actions && renderCell(actions)}
                </div>
                {details.length > 0 && (
                  <dl className="flex flex-col gap-1">
                    {details.map((cell) => (
                      <div key={cell.id} className="flex min-w-0 items-center justify-between gap-4">
                        <dt className="shrink-0 text-xs text-muted-foreground">{columnLabel(cell.column)}</dt>
                        <dd className={cn('min-w-0 truncate text-right', alignment(cell.column))}>{renderCell(cell)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            );
          })
        )}
      </ul>
      <PagingFooter paging={paging} rowCount={rowCount} />
    </div>
  );
}
